import { uiText } from '../../i18n/uiText';
import React, { useState } from 'react';
import Dialog from '../common/Dialog';
import { AlertOctagon, X } from 'lucide-react';
import { erpFlagsApi, getApiErrorMessage } from '../../api';
import { useConfirm } from '../../store/ConfirmContext';

/**
 * Кнопка «Применить санкции» (UC-9.3 FTT v1.1).
 *
 * Показывается на странице аудита при условиях:
 *   • current_user.role в {admin, auditor}
 *   • аудит имеет escalated Issue ИЛИ статус escalated/awaiting_closure
 *   • прокидывается audit (для pre-fill audit_id и company_id)
 *
 * Использование на AuditDetailPage:
 *   {audit && (currentUser.role === 'admin' || currentUser.role === 'auditor') && (
 *     <ApplySanctionsButton audit={audit} onApplied={loadAudit} />
 *   )}
 */
export default function ApplySanctionsButton({ audit, onApplied, compact = false }) {
  const [showModal, setShowModal] = useState(false);

  // Определяем company_id: meta.audited_company.id или audited_company_id
  const companyId = audit?.meta?.audited_company?.id
    || audit?.meta?.audited_company_id
    || audit?.audited_company_id
    || audit?.customer_company_id
    || null;

  if (!companyId) return null;  // нечего санкционировать

  return (
    <>
      <button type="button"
        onClick={() => setShowModal(true)}
        className="btn audit-sanctions-trigger"
        style={{
          background: '#b91c1c', color: 'white', borderColor: '#b91c1c',
          fontWeight: 600,
          padding: compact ? '6px 12px' : '8px 16px',
          fontSize: compact ? 12 : 14,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <AlertOctagon size={compact ? 14 : 16}/>{uiText("Применить санкции")}</button>
      {showModal && (
        <ApplySanctionsModal
          companyId={companyId}
          companyName={audit?.meta?.audited_company?.name || audit?.customer?.full_name || uiText("Компания #{v0}", {v0: (companyId)})}
          auditId={audit?.id}
          onClose={() => setShowModal(false)}
          onApplied={() => { setShowModal(false); onApplied && onApplied(); }}
        />
      )}
    </>
  );
}

function ApplySanctionsModal({ companyId, companyName, auditId, onClose, onApplied }) {
  const confirm = useConfirm();
  const [riskLevel, setRiskLevel] = useState('red');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setError(uiText("Укажите причину")); return; }
    if (!await confirm(
      uiText("Подтвердить выпуск санкций?\n\n") +
      `Компания: ${companyName}\n` +
      uiText("Флаг будет передан в учётную систему. Решение должно быть согласовано в команде.")
    )) return;
    setBusy(true); setError('');
    try {
      await erpFlagsApi.create({
        company_id: companyId,
        audit_id: auditId,
        risk_level: riskLevel,
        reason: reason.trim(),
      });
      onApplied();
    } catch (e2) { setError(getApiErrorMessage(e2)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog size="sm" onClose={onClose} closeDisabled={busy} labelledBy="audit-sanctions-title">
        <div className="ap-dialog__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, background: 'var(--premium-accent-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertOctagon size={18} style={{ color: 'var(--premium-danger)' }}/>
            </div>
            <div>
              <div id="audit-sanctions-title" className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Применить санкции")}</div>
              <div className="ap-dialog__description">{uiText("Передача флага в ERP/1С")}</div>
            </div>
          </div>
          <button
            type="button"
            className="ap-dialog__close"
            onClick={onClose}
            disabled={busy}
            aria-label={uiText("Закрыть окно")}
          >
            <X size={18}/>
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="ap-dialog__body">
            <div className="ap-dialog__note">
              <b>{uiText("Важно.")}</b>{uiText(" Действие нельзя отменить автоматически — флаг будет передан во внешнюю учётную систему. Само ограничение выполняется отделом закупок.")}</div>
            {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
            <div className="form-group">
              <label className="form-label">{uiText("Компания")}</label>
              <input className="ap-control" value={companyName} disabled
                />
            </div>
            <div className="form-group">
              <label className="form-label required">{uiText("Уровень риска")}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'red', label: uiText("Красный (критический)"), color: 'var(--premium-danger)' },
                  { v: 'yellow', label: uiText("Жёлтый (внимание)"), color: 'var(--premium-warning)' },
                ].map(opt => (
                  <button key={opt.v} type="button" aria-pressed={riskLevel === opt.v} onClick={() => setRiskLevel(opt.v)}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 13,
                      border: `2px solid ${riskLevel === opt.v ? opt.color : 'var(--premium-border)'}`,
                      background: riskLevel === opt.v ? 'var(--premium-surface-hover)' : 'var(--premium-surface)',
                      color: riskLevel === opt.v ? opt.color : 'var(--premium-text-secondary)',
                      fontWeight: riskLevel === opt.v ? 700 : 500, cursor: 'pointer',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label required">{uiText("Причина и обоснование")}</label>
              <textarea className="ap-control" rows={4} required
                value={reason} onChange={e => setReason(e.target.value)}
                placeholder={uiText("Например: критическое несоответствие по аудиту не устранено в установленный срок (45 дней просрочки). Согласовано на совещании.")}
              />
              <div className="ap-field__hint" style={{ marginTop: 4 }}>{uiText("Это обоснование сохраняется в журнале и передаётся в ERP вместе с флагом.")}</div>
            </div>
          </div>
          <div className="ap-dialog__footer">
            <button type="button" className="ap-button ap-button--secondary" onClick={onClose} disabled={busy}>{uiText("Отмена")}</button>
            <button type="submit" className="ap-button ap-button--danger" disabled={busy}>
              {busy ? uiText("Применяем...") : uiText("Подтвердить и передать в ERP")}
            </button>
          </div>
        </form>
    </Dialog>
  );
}
